/**
 * Shared agent/entity interaction graph builder.
 *
 * Node IDs are logical and service-qualified so that:
 * - The same agent name in two different services produces distinct nodes.
 * - The same tool name used by different services produces distinct nodes.
 * - No span IDs are ever used as graph node IDs (span topology belongs in Timeline/Tree).
 *
 * Input: API edge rows from /traces/{id}/edges or /sessions/{id}/edges.
 * Output: { layoutedNodes, baseEdges } ready for ReactFlow + layoutNodes().
 */

import { palette, graph } from './designTokens';
import { layoutNodes } from './graphLayout';

export const EDGE_KIND_LABELS = {
  SPAN_CHILD:    'Child',
  TRACE_ROOT:    'Root',
  SEMANTIC_PATH: 'Path',
  // legacy kinds preserved for old trace_edges rows
  LLM_DECISION: 'LLM Call',
  TOOL_CALL:    'Tool Call',
  HANDOFF_ATTR: 'Handoff',
  CHILD_SPAN:   'Child',
};

export const EDGE_KIND_STYLES = {
  SPAN_CHILD:    { stroke: '#555',                           strokeWidth: 1,   strokeDasharray: undefined },
  TRACE_ROOT:    { stroke: graph.nodes.entity.agent.stroke,  strokeWidth: 1.5, strokeDasharray: '4,3' },
  SEMANTIC_PATH: { stroke: graph.nodes.entity.agent.stroke,  strokeWidth: 1.5, strokeDasharray: undefined },
  LLM_DECISION:  { stroke: graph.nodes.entity.llm.stroke,   strokeWidth: 1.5, strokeDasharray: undefined },
  TOOL_CALL:     { stroke: graph.nodes.entity.tool.stroke,   strokeWidth: 1,   strokeDasharray: '5,4' },
  HANDOFF_ATTR:  { stroke: graph.nodes.entity.agent.stroke,  strokeWidth: 2,   strokeDasharray: undefined },
  CHILD_SPAN:    { stroke: '#555',                           strokeWidth: 1,   strokeDasharray: undefined },
};

/**
 * Build a service-qualified source node ID.
 * Format: <from_type>:<service_id>:<environment>:<from_name>
 */
function mkSourceId(edge) {
  const type   = (edge.from_type          || 'agent').toLowerCase();
  const svcId  = edge.source_service_id   || '';
  const env    = edge.source_environment  || '';
  const name   = edge.from_name || edge.from_agent || '';
  return `${type}:${svcId}:${env}:${name}`;
}

/**
 * Build a service-qualified target node ID.
 * Uses target service metadata when available, falls back to source service.
 * Format: <to_type>:<service_id>:<environment>:<to_name>
 */
function mkTargetId(edge) {
  const type   = (edge.to_type           || 'unspecified').toLowerCase();
  const svcId  = edge.target_service_id  || edge.source_service_id  || '';
  const env    = edge.target_environment || edge.source_environment || '';
  const name   = edge.to_name            || '';
  return `${type}:${svcId}:${env}:${name}`;
}

/**
 * Build an agent/entity interaction graph from API edge rows.
 *
 * Validation: edges with missing from_agent, missing to_name, blank computed
 * IDs, or self-edges are silently skipped — they must never reach layoutNodes.
 *
 * Returns { layoutedNodes, baseEdges }.
 */
export function buildAgentGraph(apiEdges) {
  const nodeMap        = new Map();
  const edgeGroupMap   = new Map();
  const callCountByNode = new Map();
  const tokensByNode   = new Map();

  const ensureNode = (id, name, type) => {
    if (!nodeMap.has(id)) {
      nodeMap.set(id, { id, name, type: (type || 'unspecified').toLowerCase() });
    }
  };

  apiEdges.forEach(edge => {
    const fromName = edge.from_name || edge.from_agent || '';
    if (!fromName)      return;
    if (!edge.to_name)  return;

    const sourceId = mkSourceId(edge);
    const targetId = mkTargetId(edge);

    if (!sourceId || !targetId)        return; // blank IDs crash Dagre
    if (sourceId === targetId)         return; // skip self-edges

    ensureNode(sourceId, fromName, edge.from_type || 'agent');
    ensureNode(targetId, edge.to_name,    edge.to_type || 'unspecified');

    const key = `${sourceId}||${targetId}`;
    if (!edgeGroupMap.has(key)) {
      edgeGroupMap.set(key, {
        key,
        sourceId,
        targetId,
        sourceLabel: edge.from_name || edge.from_agent || '',
        targetLabel: edge.to_name,
        kind: edge.edge_kind,
        // Service metadata — present when edges come from trace/session context
        sourceServiceName:  edge.source_service_name  || undefined,
        sourceEnvironment:  edge.source_environment   || undefined,
        targetServiceName:  edge.target_service_name  || undefined,
        targetEnvironment:  edge.target_environment   || undefined,
        isCrossService:     edge.is_cross_service     !== undefined
                              ? edge.is_cross_service
                              : undefined,
        edges: [],
      });
    }
    edgeGroupMap.get(key).edges.push(edge);

    callCountByNode.set(sourceId, (callCountByNode.get(sourceId) || 0) + 1);
    callCountByNode.set(targetId, (callCountByNode.get(targetId) || 0) + 1);
    tokensByNode.set(
      sourceId,
      (tokensByNode.get(sourceId) || 0) + (edge.input_tokens ?? 0) + (edge.output_tokens ?? 0),
    );
  });

  const targetIds = new Set([...edgeGroupMap.values()].map(g => g.targetId));

  const calleesByNode = new Map();
  const callersByNode = new Map();
  [...edgeGroupMap.values()].forEach(g => {
    if (!calleesByNode.has(g.sourceId)) calleesByNode.set(g.sourceId, []);
    calleesByNode.get(g.sourceId).push(g.targetLabel);
    if (!callersByNode.has(g.targetId)) callersByNode.set(g.targetId, []);
    callersByNode.get(g.targetId).push(g.sourceLabel);
  });

  const tempNodes = [...nodeMap.values()].map(({ id, name, type }) => ({
    id,
    type: 'graphNode',
    position: { x: 0, y: 0 },
    data: {
      id,
      label: name,
      type,
      nodeCategory: 'entity',
      isRootSpan: !targetIds.has(id),
      hasSecurityIssues: false,
      isDanger: false,
      detections: [],
      callers:    callersByNode.get(id) || [],
      callees:    calleesByNode.get(id) || [],
      totalCalls: callCountByNode.get(id) || 0,
      totalTokens: tokensByNode.get(id) || 0,
    },
  }));

  const baseEdges = [...edgeGroupMap.values()].map(group => {
    const style = EDGE_KIND_STYLES[group.kind] || EDGE_KIND_STYLES.CHILD_SPAN;
    const count = group.edges.length;
    return {
      id: group.key,
      source: group.sourceId,
      target: group.targetId,
      sourceHandle: 'bottom',
      targetHandle: 'top',
      style: {
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        strokeDasharray: style.strokeDasharray,
      },
      markerEnd: { type: 'arrowclosed', color: style.stroke },
      label: count > 1 ? `${count}×` : undefined,
      labelStyle:   { fontSize: 9, fill: palette.text.muted },
      labelBgStyle: { fill: palette.neutrals.canvas, fillOpacity: 0.85 },
      data: { ...group },
    };
  });

  const layoutedNodes = layoutNodes(tempNodes, baseEdges, 'TB');
  return { layoutedNodes, baseEdges };
}
